import React, { useMemo, useState } from 'react';
import { Accordion, Badge, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { formatDateUnified } from '../../utils/dateUtils';
import {
  buildControlsEngineerOptions,
  CONTROLS_GROUP_OPTIONS,
  filterControlsJobs,
  groupControlsJobs,
  getControlsStatusLabel,
  getControlsStatusVariant,
  getControlsSummary,
} from './utils/controlsScheduleData';

const formatDisplayDate = (value) => {
  if (!value) {
    return 'Not scheduled';
  }

  try {
    return formatDateUnified(value, 'display');
  } catch (error) {
    return value;
  }
};

const ControlsSchedule = ({ filteredJobs = [], canEdit = false }) => {
  const controlOptions = useSelector((state) => state.dropdown.userDropdowns.controlOptions || []);
  const [selectedEngineer, setSelectedEngineer] = useState('all');
  const [showCompleted, setShowCompleted] = useState(true);
  const [groupBy, setGroupBy] = useState('assignedUser');

  const engineerOptions = useMemo(
    () => buildControlsEngineerOptions(controlOptions, filteredJobs),
    [controlOptions, filteredJobs]
  );

  const visibleJobs = useMemo(
    () => filterControlsJobs(filteredJobs, { selectedEngineer, showCompleted }),
    [filteredJobs, selectedEngineer, showCompleted]
  );

  const summary = useMemo(() => getControlsSummary(visibleJobs), [visibleJobs]);
  const groupedJobs = useMemo(() => groupControlsJobs(visibleJobs, groupBy), [visibleJobs, groupBy]);

  return (
    <div className="controls-page-container">
      <div className="controls-toolbar">
        <Row className="g-3 align-items-end">
          <Col lg={4} md={6}>
            <Form.Group>
              <Form.Label>Assigned User Filter</Form.Label>
              <Form.Select value={selectedEngineer} onChange={(event) => setSelectedEngineer(event.target.value)}>
                {engineerOptions.map((engineerName) => (
                  <option key={engineerName} value={engineerName}>
                    {engineerName === 'all' ? 'All Controls Engineers' : engineerName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col lg={4} md={6}>
            <Form.Group>
              <Form.Label>Group By</Form.Label>
              <Form.Select value={groupBy} onChange={(event) => setGroupBy(event.target.value)}>
                {CONTROLS_GROUP_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col lg={2} md={6}>
            <Form.Check
              type="switch"
              id="controls-schedule-show-completed"
              label="Show completed software jobs"
              checked={showCompleted}
              onChange={(event) => setShowCompleted(event.target.checked)}
            />
          </Col>
          <Col lg={2} md={6}>
            <div className="text-md-end text-muted small">
              Editing is {canEdit ? 'enabled for this view when row-level actions are added.' : 'currently read-only.'}
            </div>
          </Col>
        </Row>
      </div>

      <Row className="g-3 mb-4">
        <Col md={3} sm={6}>
          <Card className="controls-summary-card h-100">
            <Card.Body>
              <div className="controls-summary-value">{summary.total}</div>
              <div className="controls-summary-label">Visible software jobs</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="controls-summary-card h-100">
            <Card.Body>
              <div className="controls-summary-value">{summary.active}</div>
              <div className="controls-summary-label">Active jobs</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="controls-summary-card h-100">
            <Card.Body>
              <div className="controls-summary-value">{summary.completed}</div>
              <div className="controls-summary-label">Completed jobs</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6}>
          <Card className="controls-summary-card h-100">
            <Card.Body>
              <div className="controls-summary-value">{summary.unscheduled}</div>
              <div className="controls-summary-label">Without software due date</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {groupedJobs.length === 0 ? (
        <div className="alert alert-info">No controls jobs match the current filters.</div>
      ) : (
        <Accordion alwaysOpen>
          {groupedJobs.map((group) => {
            const jobs = group.jobs || [];
            const completedCount = jobs.filter((job) => getControlsStatusLabel(job) === 'Complete').length;

            return (
              <Accordion.Item eventKey={group.key} key={group.key}>
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-3">
                    <div>{group.label}</div>
                    <div>
                      <Badge bg="secondary" pill className="me-2">
                        {jobs.length} job{jobs.length === 1 ? '' : 's'}
                      </Badge>
                      {group.isCompletedGroup ? (
                        <Badge bg="success" pill>
                          {completedCount} complete
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="table-responsive">
                    <Table bordered hover className="controls-table align-middle">
                      <thead>
                        <tr>
                          <th>Job Number</th>
                          <th>Project Name</th>
                          <th>Model Number</th>
                          <th>Software Type</th>
                          <th>Software Due</th>
                          <th>Status</th>
                          <th>Software Done</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobs.map((job) => {
                          const statusLabel = getControlsStatusLabel(job);
                          const rowClassName = statusLabel === 'Complete' ? 'complete-row' : 'active-row';

                          return (
                            <tr key={`${job.ID}-${job.JobNumber}`} className={rowClassName}>
                              <td>{job.JobNumber || 'Unknown'}</td>
                              <td>{job.ProjectName || 'Untitled project'}</td>
                              <td>{job.ModelNumber || '-'}</td>
                              <td>{job.SoftwareType || '-'}</td>
                              <td>{formatDisplayDate(job.SoftwareDue)}</td>
                              <td>
                                <Badge bg={getControlsStatusVariant(job)}>{statusLabel}</Badge>
                              </td>
                              <td>{formatDisplayDate(job.SoftwareDone)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}
    </div>
  );
};

export default ControlsSchedule;